import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/Tile';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import EsriJSON from 'ol/format/EsriJSON';
import { tile as tileStrategy } from 'ol/loadingstrategy';
import { createXYZ } from 'ol/tilegrid';
import { toLonLat, transformExtent } from 'ol/proj';
import {
  extend,
  getCenter,
  getIntersection,
  intersects,
  isEmpty,
} from 'ol/extent';
import { writeArrayBuffer } from 'geotiff';
import GeoJSON from 'ol/format/GeoJSON';
import JSZip from 'jszip';
import {
  Circle as CircleStyle,
  Fill,
  Icon,
  Stroke,
  Style,
  Text,
} from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { unByKey } from 'ol/Observable';
import config from './config';
import decodeArcGISPbf from 'arcgis-pbf-parser';
import CALIFORNIA_OUTLINE from './californiaOutline';

const esrijsonFormat = new EsriJSON();

// ArcGIS caps every /query response at the layer's maxRecordCount (often
// 2000) and reports exceededTransferLimit:true rather than erroring, so a
// naive single request silently drops everything past the first page. This
// caps how many features we'll page through per tile so a single tile over
// a huge layer (e.g. a statewide dataset with 1M+ features) can't require
// dozens of sequential round trips.
const MAX_FEATURES_PER_EXTENT = 6000;

// A clip-to-boundary or download request is deliberate and one-shot (the
// user picked a specific, bounded area), rather than incidental viewport
// panning, so it's worth paginating further before giving up.
const MAX_FEATURES_PER_CLIP = 50000;

// Below this zoom level (i.e. at a coarser resolution than this, in map
// units per pixel) we don't query the layer at all. 400 is roughly zoom 8.6
// (a few counties across); the default view (~1221, zoom 7) and the reset
// button (zoom 6.2) are both zoomed out past it, and ForestDataMapZoomNotice
// tells the user to zoom in. Zoomed out further, dense areas hit
// MAX_FEATURES_PER_EXTENT and show a capped, partial sample per tile.
const MIN_RESOLUTION_TO_LOAD = 400;

// Per-layer overrides of MIN_RESOLUTION_TO_LOAD, keyed by the layer URL (the
// CKAN resource url). For layers so dense that a 512px tile at the default
// holds more than MAX_FEATURES_PER_EXTENT features and only partly loads.
const LAYER_MIN_RESOLUTION_TO_LOAD = {
  // ~1.2M polygons: a dense 204km tile (res 400) holds ~29k features, a
  // 51km tile (res 100, ~zoom 10.6) ~3.9k - under the 6000 cap.
  'https://services1.arcgis.com/8CpMUd3fdw6aXef7/arcgis/rest/services/California_Fire_Hazard_Severity_Zones/FeatureServer/3': 100,
};

const getMinResolutionToLoad = (serviceUrl) =>
  LAYER_MIN_RESOLUTION_TO_LOAD[serviceUrl?.replace(/\/+$/, '')] ??
  MIN_RESOLUTION_TO_LOAD;

const xyzTileStrategy = tileStrategy(createXYZ({ tileSize: 512 }));

// Wraps the tile strategy so that, when too zoomed out, it reports there is
// NOTHING to load at all (an empty extent list) rather than letting the
// loader itself report zero features for a huge coarse tile. Those aren't
// equivalent: ol/source/Vector marks whatever extent it hands to the loader
// as "loaded" the moment the loader is invoked, regardless of what the
// loader returns. A coarse zoomed-out tile geographically CONTAINS every
// finer tile within it, and ol's dedup check is purely extent-containment
// (it doesn't know about resolution/zoom level) - so a single huge tile
// marked "loaded" at low zoom permanently blocks any finer reload of that
// whole area later, no matter how far in you zoom. Only gating in the
// strategy (before ol commits to "loading" anything) avoids that trap.
const gatedTileStrategy =
  (minResolutionToLoad) => (extent, resolution, projection) => {
    if (resolution > minResolutionToLoad) {
      return [];
    }
    return xyzTileStrategy(extent, resolution, projection);
  };

// Builds the /query POST body for an ArcGIS FeatureServer/MapServer layer
// request (minus resultOffset, which callers set per page). `geometry` is
// either an esriGeometryEnvelope object ({xmin,ymin,xmax,ymax,
// spatialReference}) or an esriGeometryPolygon object ({rings,
// spatialReference}). A clip boundary's polygon can have thousands of
// vertices (e.g. a county traced at high resolution) - as a GET query
// param that's a 100KB+ URL, which real-world proxies/servers reject
// (confirmed against this exact service: a ~200KB GET URL for LA County's
// boundary was rejected outright). POST avoids any URL-length limit.
const buildArcGISQueryParams = ({
  geometry,
  geometryType,
  srid,
  format = 'json',
}) => {
  return new URLSearchParams({
    f: format,
    returnGeometry: 'true',
    spatialRel: 'esriSpatialRelIntersects',
    geometry: JSON.stringify(geometry),
    geometryType,
    inSR: String(srid),
    outFields: '*',
    outSR: String(srid),
  });
};

// Pages through an ArcGIS /query request (POSTing the params built by
// buildArcGISQueryParams), accumulating the raw response features across
// pages while ArcGIS reports truncation, up to maxFeatures. ArcGIS caps
// every response at the layer's maxRecordCount (often 2000) and signals it
// via exceededTransferLimit rather than erroring - at the top level for
// f=json, nested under properties for f=geojson - so a naive single
// request silently drops everything past the first page. Returns the raw
// feature objects unparsed (EsriJSON or GeoJSON Feature shape, depending on
// `format`) so callers decide how to use them, plus whether the cap was hit
// and the layer's object id field (see readEsriFeatures).
const fetchArcGISPages = async (serviceUrl, params, maxFeatures) => {
  let offset = 0;
  let rawFeatures = [];
  for (;;) {
    params.set('resultOffset', String(offset));
    const response = await fetch(`${serviceUrl}/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    const json = await response.json();
    if (json.error) {
      throw new Error(json.error.message || 'ArcGIS query failed');
    }
    const pageFeatures = json.features || [];
    rawFeatures = rawFeatures.concat(pageFeatures);
    const truncated = Boolean(
      json.exceededTransferLimit ?? json.properties?.exceededTransferLimit
    );
    if (
      truncated &&
      pageFeatures.length > 0 &&
      rawFeatures.length < maxFeatures
    ) {
      offset += pageFeatures.length;
      continue;
    }
    return {
      rawFeatures,
      truncated,
      objectIdFieldName: json.objectIdFieldName,
    };
  }
};

// Parses EsriJSON features with their object ids set. ol/source/Vector only
// drops duplicates by id, and a feature crossing a tile edge is returned by
// every tile's envelope query - without ids each copy is added and drawn.
const readEsriFeatures = (rawFeatures, objectIdFieldName, readOptions) =>
  esrijsonFormat.readFeatures(
    { features: rawFeatures, objectIdFieldName },
    readOptions
  );

// Converts an ol/geom Polygon or MultiPolygon (as used for clip boundaries
// throughout this app) into the {rings} shape esriGeometryPolygon expects.
// Esri has no separate multi-polygon type - a polygon geometry's rings can
// represent multiple disjoint areas (or holes) in one list.
const olGeometryToEsriRings = (geometry) => {
  const type = geometry.getType();
  if (type === 'Polygon') return geometry.getCoordinates();
  if (type === 'MultiPolygon') return geometry.getCoordinates().flat();
  throw new Error(`Unsupported clip geometry type: ${type}`);
};

// Builds an ol/source/Vector that lazily loads features from an ArcGIS
// FeatureServer/MapServer layer as the map view's extent changes, by
// querying the layer's own /query endpoint (geometry envelope intersect)
// and parsing the EsriJSON response. ArcGIS reprojects server-side, so we
// just pass the map's current projection straight through as inSR/outSR.
const createArcGISVectorSource = (serviceUrl) => {
  const minResolutionToLoad = getMinResolutionToLoad(serviceUrl);
  const vectorSource = new VectorSource({
    // A tile grid (not the plain bbox strategy) so already-loaded areas are
    // cached and never refetched just from panning/zooming slightly - bbox
    // has no such caching and re-requests the full current view (including
    // re-paginating it) on every tiny view change, which for a layer this
    // size looks like it never stops loading.
    strategy: gatedTileStrategy(minResolutionToLoad),
    loader: function (extent, resolution, projection, success, failure) {
      const srid = projection
        .getCode()
        .split(/:(?=\d+$)/)
        .pop();
      const geometry = {
        xmin: extent[0],
        ymin: extent[1],
        xmax: extent[2],
        ymax: extent[3],
        spatialReference: { wkid: Number(srid) },
      };
      const params = buildArcGISQueryParams({
        geometry,
        geometryType: 'esriGeometryEnvelope',
        srid,
      });

      fetchArcGISPages(serviceUrl, params, MAX_FEATURES_PER_EXTENT)
        .then(({ rawFeatures, truncated, objectIdFieldName }) => {
          const features = readEsriFeatures(rawFeatures, objectIdFieldName, {
            featureProjection: projection,
            dataProjection: `EPSG:${srid}`,
          });
          if (truncated) {
            console.warn(
              `ArcGIS layer ${serviceUrl} has more features in view than the ${MAX_FEATURES_PER_EXTENT}-feature-per-view cap; zoom in to see everything.`
            );
          }
          vectorSource.addFeatures(features);
          success(features);
        })
        .catch(() => failure());
    },
  });
  // Lets getLayersNeedingZoom find layers that gatedTileStrategy is holding back.
  vectorSource.set('minResolutionToLoad', minResolutionToLoad);
  return vectorSource;
};

// Visible layers that load nothing at the current zoom because
// gatedTileStrategy is holding them back - used to tell the user to zoom in.
const getLayersNeedingZoom = (map) => {
  const resolution = map.getView()?.getResolution();
  if (resolution === undefined) return [];
  return map.getAllLayers().filter((layer) => {
    const source = layer.getSource();
    const minResolutionToLoad = source?.get('minResolutionToLoad');
    return (
      layer.getVisible() &&
      minResolutionToLoad !== undefined &&
      resolution > minResolutionToLoad &&
      // Lighter layers draw simplified features instead (attachArcGISOverview).
      !source.get('overviewDrawsFeatures')
    );
  });
};

// Overview markers drawn in place of a gated layer's features while it's too
// zoomed out to load them (see gatedTileStrategy): the view is split into a
// grid of cells, each loaded and drawn independently, in one of two modes
// (see attachArcGISOverview):
//
// - "features" for lighter layers: simplified geometry for each 512px cell,
//   fetched the way ArcGIS's own map viewer does it (see
//   fetchArcGISTileFeatures), drawn with the layer's own style.
// - "counts" for layers too big even for that: one circle per
//   OVERVIEW_CELL_SIZE px cell, sized and labeled by roughly how many
//   features fall in it, from returnCountOnly queries. Cells are trimmed to
//   California (see californiaCellPart), so markers stay in the state and
//   border cells only count what's on the California side.
const OVERVIEW_CELL_SIZE = 160;
const OVERVIEW_TILE_SIZE = 512;
const OVERVIEW_QUERY_CONCURRENCY = 6;
const OVERVIEW_COLOR = '#3B0875';
// Count markers draw above every data layer (and the boundary highlight at
// 999) so they stay readable over whatever else is turned on.
const OVERVIEW_MARKER_Z_INDEX = 1000;

// Layers with up to this many features get the "features" overview. Above
// it, a statewide view would be dropping most features to the per-tile
// record cap anyway (the Fire Hazard Severity Zones layer has ~1.2M).
const LIGHT_LAYER_MAX_FEATURES = 250000;

// ArcGIS intermittently fails overview queries while its cache is cold
// (several big cells at once), so retry a couple of times before leaving a
// cell empty until the next pan/zoom.
const OVERVIEW_QUERY_ATTEMPTS = 3;

const withRetries = async (load) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await load();
    } catch (error) {
      if (attempt >= OVERVIEW_QUERY_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
};

const extentToEsriEnvelope = (extent, srid) => ({
  xmin: extent[0],
  ymin: extent[1],
  xmax: extent[2],
  ymax: extent[3],
  spatialReference: { wkid: Number(srid) },
});

const fetchArcGISCount = (serviceUrl, extent, srid) =>
  withRetries(() => fetchArcGISCountOnce(serviceUrl, extent, srid));

const fetchArcGISCountOnce = async (serviceUrl, extent, srid) => {
  const params = new URLSearchParams({
    f: 'json',
    returnCountOnly: 'true',
    // Bounding-box test only: an exact intersects count over a large cell
    // of a dense polygon layer takes ArcGIS 10-30s+ and can time out, while
    // this answers in ~1s and overcounts by only a few percent - fine for
    // an approximate overview.
    spatialRel: 'esriSpatialRelEnvelopeIntersects',
    geometry: JSON.stringify(extentToEsriEnvelope(extent, srid)),
    geometryType: 'esriGeometryEnvelope',
    inSR: String(srid),
  });
  const response = await fetch(`${serviceUrl}/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  const json = await response.json();
  if (json.error) {
    throw new Error(
      json.error.message || `ArcGIS count query failed (${json.error.code})`
    );
  }
  return json.count ?? 0;
};

// Fetches one overview tile's features the way ArcGIS's own map viewer
// does, which is what lets it draw a whole lighter layer from far out:
// - f=pbf: compact binary instead of verbose EsriJSON.
// - maxAllowableOffset + quantizationParameters (one pixel at this zoom):
//   the server simplifies away detail finer than a pixel.
// - resultType=tile: the server drops sub-pixel features and caps the
//   response at the layer's tileMaxRecordCount (typically 4000).
// - orderByFields largest-first, so what that cap cuts is the smallest.
// That's ~250KB for 4000 polygons, vs ~1.4MB of EsriJSON for 2000.
// Returned geometries aren't clipped to the tile, so features straddling
// tiles come back from each - the same ids, which the source dedupes.
const fetchArcGISTileFeatures = async ({
  serviceUrl,
  info,
  extent,
  srid,
  resolution,
}) => {
  const envelope = extentToEsriEnvelope(extent, srid);
  const params = new URLSearchParams({
    f: 'pbf',
    where: '1=1',
    outFields: info.outFields.join(','),
    geometry: JSON.stringify(envelope),
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    inSR: String(srid),
    outSR: String(srid),
    resultType: 'tile',
    maxAllowableOffset: String(resolution),
    quantizationParameters: JSON.stringify({
      mode: 'view',
      originPosition: 'upperLeft',
      tolerance: resolution,
      extent: envelope,
    }),
  });
  if (info.areaField) params.set('orderByFields', `${info.areaField} DESC`);
  const response = await fetch(`${serviceUrl}/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  // Errors come back as JSON even when asking for pbf.
  if (!response.ok || /json/.test(response.headers.get('content-type'))) {
    const json = await response.json().catch(() => ({}));
    throw new Error(
      json.error?.message || `ArcGIS tile query failed (${response.status})`
    );
  }
  const { featureCollection } = decodeArcGISPbf(
    new Uint8Array(await response.arrayBuffer())
  );
  // Already in the map projection (outSR), so no reprojection.
  return new GeoJSON().readFeatures(featureCollection);
};

// serviceUrl -> Promise of what attachArcGISOverview needs to pick a mode:
// whether the layer is light enough (and its server new enough) for the
// "features" overview, and the fields fetchArcGISTileFeatures asks for.
const overviewInfoCache = new Map();
const getArcGISOverviewInfo = (serviceUrl, colorMap) => {
  if (!overviewInfoCache.has(serviceUrl)) {
    const total = new URLSearchParams({
      f: 'json',
      where: '1=1',
      returnCountOnly: 'true',
    });
    overviewInfoCache.set(
      serviceUrl,
      Promise.all([
        fetch(`${serviceUrl}?f=json`).then((r) => r.json()),
        fetch(`${serviceUrl}/query?${total}`).then((r) => r.json()),
      ])
        .then(([meta, { count }]) => ({
          light:
            count !== undefined &&
            count <= LIGHT_LAYER_MAX_FEATURES &&
            /\bpbf\b/i.test(meta.supportedQueryFormats ?? ''),
          areaField: meta.geometryProperties?.shapeAreaFieldName,
          // The layer's full extent ({xmin, ..., spatialReference}).
          extent: meta.extent,
          // Just what the layer's style reads, plus the id for dedupe.
          outFields: [
            meta.objectIdField,
            colorMap?.field,
            ...(colorMap?.fields ?? []),
          ].filter(Boolean),
        }))
        // If we can't tell, counts work for any layer.
        .catch(() => ({ light: false, outFields: [] }))
    );
  }
  return overviewInfoCache.get(serviceUrl);
};

// Sutherland-Hodgman: clips a polygon ring to an axis-aligned extent.
const clipRingToExtent = (ring, [minX, minY, maxX, maxY]) => {
  const crossAtX = (a, b, x) => [
    x,
    a[1] + ((x - a[0]) / (b[0] - a[0])) * (b[1] - a[1]),
  ];
  const crossAtY = (a, b, y) => [
    a[0] + ((y - a[1]) / (b[1] - a[1])) * (b[0] - a[0]),
    y,
  ];
  const edges = [
    [(p) => p[0] >= minX, (a, b) => crossAtX(a, b, minX)],
    [(p) => p[0] <= maxX, (a, b) => crossAtX(a, b, maxX)],
    [(p) => p[1] >= minY, (a, b) => crossAtY(a, b, minY)],
    [(p) => p[1] <= maxY, (a, b) => crossAtY(a, b, maxY)],
  ];
  let out = ring;
  for (const [inside, cross] of edges) {
    const input = out;
    out = [];
    input.forEach((current, i) => {
      const previous = input[(i + input.length - 1) % input.length];
      if (inside(current)) {
        if (!inside(previous)) out.push(cross(previous, current));
        out.push(current);
      } else if (inside(previous)) {
        out.push(cross(previous, current));
      }
    });
    if (out.length === 0) break;
  }
  return out;
};

// projection code -> California outline as an ol MultiPolygon in it.
const californiaOutlines = new Map();
const getCaliforniaOutline = (projection) => {
  const code = projection.getCode();
  if (!californiaOutlines.has(code)) {
    californiaOutlines.set(
      code,
      new MultiPolygon(CALIFORNIA_OUTLINE).transform('EPSG:4326', projection)
    );
  }
  return californiaOutlines.get(code);
};

// The California part of an overview cell: where to put its marker (a point
// inside the state, so border cells don't sit over Nevada or the ocean) and
// the extent to count features in (the in-state part's bounding box). Null
// when the cell is entirely outside California.
const californiaCellPart = (cellExtent, projection) => {
  const outline = getCaliforniaOutline(projection);
  if (!intersects(outline.getExtent(), cellExtent)) return null;
  let queryExtent = null;
  let largest = null;
  outline.getPolygons().forEach((polygon) => {
    // The outline has no holes, so the outer ring is the whole polygon.
    const ring = clipRingToExtent(polygon.getCoordinates()[0], cellExtent);
    if (ring.length < 3) return;
    const piece = new Polygon([ring]);
    const area = piece.getArea();
    if (area <= 0) return;
    queryExtent = queryExtent
      ? extend(queryExtent, piece.getExtent())
      : piece.getExtent().slice(); // extend() mutates its first argument
    if (!largest || area > largest.area) largest = { piece, area };
  });
  if (!largest) return null;
  // The interior point of a box-clipped, possibly concave piece is nearly
  // always in the state; fall back to its center if it isn't.
  const interior = largest.piece.getInteriorPoint().getCoordinates();
  const position = outline.intersectsCoordinate(interior)
    ? [interior[0], interior[1]]
    : getCenter(largest.piece.getExtent());
  return { position, queryExtent };
};

const formatOverviewCount = (count) => {
  if (count >= 1e6) return `${(count / 1e6).toFixed(count < 1e7 ? 1 : 0)}M`;
  if (count >= 1e3) return `${(count / 1e3).toFixed(count < 1e4 ? 1 : 0)}k`;
  return String(count);
};

// serviceUrl -> Map of "z/x/y" -> the cell's count or features (or a pending
// Promise). Shared across overview layers because the layer tree rebuilds a
// layer when it's toggled or clipped, and a service's data doesn't change
// between rebuilds.
const overviewCellCache = new Map();

const overviewStyleCache = new Map();
const overviewStyle = (feature) => {
  const count = feature.get('count');
  if (!overviewStyleCache.has(count)) {
    overviewStyleCache.set(
      count,
      new Style({
        image: new CircleStyle({
          // 1 -> 8px, 1k -> 15.5px, 100k -> 20.5px
          radius: 8 + 2.5 * Math.log10(count),
          fill: new Fill({ color: hexToRgba(OVERVIEW_COLOR, 0.8) }),
          stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
        }),
        text: new Text({
          text: formatOverviewCount(count),
          font: '600 11px sans-serif',
          fill: new Fill({ color: '#ffffff' }),
        }),
      })
    );
  }
  return overviewStyleCache.get(count);
};

// Gives an ArcGIS vector layer built on createArcGISVectorSource a companion
// overview layer, shown only while zoomed out past the source's
// minResolutionToLoad (see the modes above). The overview is unmanaged
// (layer.setMap, not map.addLayer) so the layer tree, popover and download
// code - which all walk map.getLayers() - never see it; it instead follows
// the main layer's map, visibility and opacity. Layers on other sources
// (e.g. clipped ones, which load everything at any zoom) are left alone.
//
// The overview keeps its own per-zoom cells rather than a second tiled
// VectorSource: ol's loaded-extent dedupe ignores zoom (see
// gatedTileStrategy), so a source that loaded coarse, simplified tiles far
// out would never refetch finer ones nearer in.
const attachArcGISOverview = (layer, colorMap) => {
  const minResolutionToLoad = layer.getSource()?.get('minResolutionToLoad');
  const serviceUrl = layer.get('service_url');
  if (minResolutionToLoad === undefined || !serviceUrl) return layer;
  getArcGISOverviewInfo(serviceUrl, colorMap).then((info) =>
    startArcGISOverview(layer, serviceUrl, minResolutionToLoad, info)
  );
  return layer;
};

const startArcGISOverview = (layer, serviceUrl, minResolutionToLoad, info) => {
  const mode = info.light ? 'features' : 'counts';
  const source = new VectorSource();
  const overview = new VectorLayer({
    source,
    style: mode === 'features' ? layer.getStyle() : overviewStyle,
    // A layer shows at resolution >= minResolution, but the gated source
    // already loads at exactly minResolutionToLoad (where the zoom notice and
    // marker clicks land), so hide the overview there too.
    minResolution: minResolutionToLoad * 1.0001,
    arcgis_overview: mode,
    visible: layer.getVisible(),
  });
  layer.set('arcgis_overview_layer', overview);
  // Simplified features stack and fade like their layer. Count markers sit
  // on top at full strength - see-through circles over other layers' fills
  // are hard to read - and only follow the opacity slider to hide at 0.
  const syncStacking = () => {
    if (mode === 'features') {
      // Unset would mean Infinity for an unmanaged layer - above the markers.
      overview.setZIndex(layer.getZIndex() ?? 0);
      overview.setOpacity(layer.getOpacity());
    } else {
      overview.setZIndex(OVERVIEW_MARKER_Z_INDEX);
      overview.setOpacity(layer.getOpacity() > 0 ? 1 : 0);
    }
  };
  syncStacking();
  // Lets getLayersNeedingZoom skip it - nothing's missing when zoomed out.
  if (mode === 'features') layer.getSource().set('overviewDrawsFeatures', true);
  if (!overviewCellCache.has(serviceUrl)) {
    overviewCellCache.set(serviceUrl, new Map());
  }
  const cells = overviewCellCache.get(serviceUrl);
  let grid = null;
  let projection = null;
  let shownZ = null;
  // "z/x/y" -> where the cell's marker goes and what extent to query, or
  // null to skip the cell (counts outside California). Memoized because
  // californiaCellPart clips polygons.
  const cellParts = new Map();
  const getCellPart = (key) => {
    if (!cellParts.has(key)) {
      const cellExtent = grid.getTileCoordExtent(key.split('/').map(Number));
      cellParts.set(
        key,
        mode === 'features'
          ? { position: getCenter(cellExtent), queryExtent: cellExtent }
          : californiaCellPart(cellExtent, projection)
      );
    }
    return cellParts.get(key);
  };
  let mapKeys = [];
  let queue = [];
  let activeWorkers = 0;

  const showCell = (key, data) => {
    const z = Number(key.split('/')[0]);
    if (z !== shownZ) return;
    if (mode === 'features') {
      source.addFeatures(data); // features already shown (by id) are skipped
    } else if (data > 0 && !source.getFeatureById(key)) {
      const feature = new Feature({
        geometry: new Point(getCellPart(key).position),
        count: data,
      });
      feature.setId(key);
      source.addFeature(feature);
    }
  };

  const loadCell = (key, srid) => {
    const { queryExtent } = getCellPart(key);
    if (mode === 'counts') {
      return fetchArcGISCount(serviceUrl, queryExtent, srid);
    }
    return withRetries(() =>
      fetchArcGISTileFeatures({
        serviceUrl,
        info,
        extent: queryExtent,
        srid,
        resolution: grid.getResolution(Number(key.split('/')[0])),
      })
    );
  };

  const update = (map) => {
    const view = map.getView();
    const resolution = view.getResolution();
    if (
      !layer.getVisible() ||
      !map.getSize() ||
      resolution === undefined ||
      resolution <= minResolutionToLoad
    ) {
      queue = [];
      return;
    }
    projection = view.getProjection();
    // A 512px cell's grid resolution is one map pixel, which is what
    // fetchArcGISTileFeatures simplifies to.
    grid ??= createXYZ({
      extent: projection.getExtent(),
      tileSize: mode === 'features' ? OVERVIEW_TILE_SIZE : OVERVIEW_CELL_SIZE,
    });
    const srid = projection
      .getCode()
      .split(/:(?=\d+$)/)
      .pop();
    const z = grid.getZForResolution(resolution);
    if (z !== shownZ) {
      source.clear();
      shownZ = z;
    }

    const extent = view.calculateExtent(map.getSize());
    const toFetch = [];
    grid.forEachTileCoord(extent, z, ([cz, x, y]) => {
      const key = `${cz}/${x}/${y}`;
      if (!getCellPart(key)) return;
      const cached = cells.get(key);
      if (cached === undefined) {
        toFetch.push(key);
      } else {
        // Pending cells may belong to another overview of this service.
        Promise.resolve(cached).then(
          (data) => showCell(key, data),
          () => {}
        );
      }
    });

    // Fetch nearest-the-center cells first, a few at a time, so a big view
    // fills in from the middle. Each move replaces the queue rather than
    // adding to it: some queries take ArcGIS 10s+ on a cold cache, and cells
    // from views already panned/zoomed away from shouldn't hold up this one.
    const viewCenter = view.getCenter();
    const distance = (key) => {
      const c = getCellPart(key).position;
      return (c[0] - viewCenter[0]) ** 2 + (c[1] - viewCenter[1]) ** 2;
    };
    queue = toFetch.sort((a, b) => distance(a) - distance(b));
    while (activeWorkers < OVERVIEW_QUERY_CONCURRENCY && queue.length) {
      worker(srid);
    }
  };

  const worker = async (srid) => {
    activeWorkers++;
    while (queue.length) {
      const key = queue.shift();
      if (cells.has(key)) continue;
      const pending = loadCell(key, srid);
      cells.set(key, pending);
      try {
        const data = await pending;
        cells.set(key, data);
        showCell(key, data);
      } catch (error) {
        cells.delete(key); // retry on a later move
        console.error('Failed to load ArcGIS overview cell:', error);
      }
    }
    activeWorkers--;
  };

  const attach = (map) => {
    unByKey(mapKeys);
    mapKeys = [];
    overview.setMap(map ?? null);
    if (!map) return;
    mapKeys = [
      map.on('moveend', () => update(map)),
      // The map's size can change (sidebar, window) without any moveend.
      map.on('change:size', () => update(map)),
    ];
    if (mode === 'counts') {
      // Clicking a count marker zooms in to where its features load.
      mapKeys.push(
        map.on('singleclick', (evt) => {
          const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
            layerFilter: (l) => l === overview,
          });
          if (!feature) return;
          map.getView().animate({
            center: feature.getGeometry().getCoordinates(),
            resolution: minResolutionToLoad,
            duration: 400,
          });
        })
      );
    }
    update(map);
  };

  layer.on('change:map', () => attach(layer.get('map')));
  layer.on('change:visible', () => {
    overview.setVisible(layer.getVisible());
    const map = layer.get('map');
    if (map) update(map);
  });
  layer.on(['change:opacity', 'change:zIndex'], syncStacking);
  if (layer.get('map')) attach(layer.get('map'));
};

// One-shot (not lazily tiled) ol/source/Vector holding every feature that
// intersects a clip geometry, for "clip to boundary" map rendering. Unlike
// createArcGISVectorSource this loads once, not per-viewport-tile, since
// the whole point is showing everything within a user-chosen bounded area
// regardless of pan/zoom.
const createClippedArcGISVectorSource = (serviceUrl, clipGeometry, srid) => {
  const vectorSource = new VectorSource();
  queryArcGISFeaturesInGeometry({
    serviceUrl,
    clipGeometry,
    srid,
    format: 'json',
  })
    .then(({ rawFeatures, truncated, objectIdFieldName }) => {
      const features = readEsriFeatures(rawFeatures, objectIdFieldName, {
        featureProjection: `EPSG:${srid}`,
        dataProjection: `EPSG:${srid}`,
      });
      if (truncated) {
        console.warn(
          `ArcGIS layer ${serviceUrl} has more features in the clip boundary than the ${MAX_FEATURES_PER_CLIP}-feature cap; showing a partial sample.`
        );
      }
      vectorSource.addFeatures(features);
    })
    .catch((error) =>
      console.error('Failed to load clipped ArcGIS features:', error)
    );
  return vectorSource;
};

// Queries an ArcGIS layer for features intersecting a clip geometry (an
// ol/geom Polygon/MultiPolygon in the given srid), paginating as needed.
// `format` is 'json' (EsriJSON, for building ol Features - see
// createClippedArcGISVectorSource) or 'geojson' (a plain FeatureCollection,
// for zipping into a download - see LayerTreeControlPanel's handleDownload).
const queryArcGISFeaturesInGeometry = ({
  serviceUrl,
  clipGeometry,
  srid,
  format = 'json',
  maxFeatures = MAX_FEATURES_PER_CLIP,
}) => {
  const geometry = {
    rings: olGeometryToEsriRings(clipGeometry),
    spatialReference: { wkid: Number(srid) },
  };
  const params = buildArcGISQueryParams({
    geometry,
    geometryType: 'esriGeometryPolygon',
    srid,
    format,
  });
  return fetchArcGISPages(serviceUrl, params, maxFeatures);
};

// Used for ArcGIS layers with no ingested legend (and legend classes a
// feature doesn't match). The image is what point layers draw - stroke and
// fill alone only cover lines and polygons.
const arcGISDefaultStyle = new Style({
  stroke: new Stroke({ color: '#3B0875', width: 1 }),
  fill: new Fill({ color: 'rgba(59, 8, 117, 0.2)' }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: 'rgba(59, 8, 117, 0.6)' }),
    stroke: new Stroke({ color: '#3B0875', width: 1 }),
  }),
});

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// One ingested legend entry -> an ol Style. Picture-marker points (esriPMS)
// carry their icon as a data: URL; everything else is a color, used for the
// line/polygon outline and fill and for a circle marker on points.
const styleForLegendEntry = (entry) => {
  if (entry.image) {
    return new Style({ image: new Icon({ src: entry.image }) });
  }
  if (!entry.color) return arcGISDefaultStyle;
  return new Style({
    stroke: new Stroke({ color: entry.color, width: 1 }),
    fill: new Fill({ color: hexToRgba(entry.color, 0.5) }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: entry.color }),
      stroke: new Stroke({ color: '#ffffff', width: 1 }),
    }),
  });
};

// Builds a per-feature style function from an ArcGIS categorical legend
// (see createArcGISVectorSource / getGisService's color_map), coloring each
// feature by its class field's value. Falls back to a flat style when no
// legend was ingested for this dataset.
//
// Features matching no class follow the renderer's default: its symbol, or
// hidden (null style) when ArcGIS has none, as ArcGIS itself draws them.
// Legends ingested before `default` was recorded have no key, so those keep
// the flat fallback.
const buildArcGISStyle = (rawColorMap, colorOverrides) => {
  const colorMap = applyArcGISColorOverrides(rawColorMap, colorOverrides);
  if (
    !colorMap ||
    colorMap.type !== 'categorical' ||
    !colorMap.entries?.length
  ) {
    return arcGISDefaultStyle;
  }
  if (!colorMap.field) {
    // Ingested from a simple renderer: every feature looks the same.
    return styleForLegendEntry(colorMap.entries[0]);
  }
  const styleByValue = {};
  colorMap.entries.forEach((entry) => {
    styleByValue[entry.value] = styleForLegendEntry(entry);
  });
  let unmatchedStyle = arcGISDefaultStyle;
  if ('default' in colorMap) {
    unmatchedStyle = colorMap.default
      ? styleForLegendEntry(colorMap.default)
      : null;
  }
  return (feature) => {
    const value = colorMap.fields
      ? colorMap.fields
          .map((field) => feature.get(field))
          .join(colorMap.delimiter ?? ',')
      : String(feature.get(colorMap.field));
    return styleByValue[value] || unmatchedStyle;
  };
};

// Keys into a layer's color overrides ({key: '#rrggbb'}, set from
// ForestDataMapLegend) for the entries that aren't a class value.
const ALL_FEATURES_KEY = '__all__';
const DEFAULT_ENTRY_KEY = '__default__';

// The recolorable entries of an ArcGIS layer's legend, each with the key its
// override is stored under. Layers with no ingested legend get a single
// entry for the flat default color, so they can be recolored too.
const getArcGISLegendEntries = (colorMap) => {
  if (
    !colorMap ||
    colorMap.type !== 'categorical' ||
    !colorMap.entries?.length
  ) {
    return [
      {
        key: ALL_FEATURES_KEY,
        label: 'All features',
        color: arcGISDefaultStyle.getStroke().getColor(),
      },
    ];
  }
  const entries = colorMap.field
    ? colorMap.entries.map((entry) => ({ ...entry, key: String(entry.value) }))
    : [{ ...colorMap.entries[0], key: ALL_FEATURES_KEY }];
  if (colorMap.default) {
    entries.push({ ...colorMap.default, key: DEFAULT_ENTRY_KEY });
  }
  return entries;
};

// The layer's legend with the user's recolors applied, for buildArcGISStyle.
// A recolored picture marker draws as a circle in the new color.
const applyArcGISColorOverrides = (colorMap, overrides) => {
  if (!overrides || Object.keys(overrides).length === 0) return colorMap;
  const recolor = (entry, key) =>
    overrides[key]
      ? { ...entry, color: overrides[key], image: undefined }
      : entry;
  if (
    !colorMap ||
    colorMap.type !== 'categorical' ||
    !colorMap.entries?.length
  ) {
    if (!overrides[ALL_FEATURES_KEY]) return colorMap;
    return {
      type: 'categorical',
      entries: [{ color: overrides[ALL_FEATURES_KEY] }],
    };
  }
  return {
    ...colorMap,
    entries: colorMap.field
      ? colorMap.entries.map((entry) => recolor(entry, String(entry.value)))
      : [recolor(colorMap.entries[0], ALL_FEATURES_KEY)],
    ...(colorMap.default
      ? { default: recolor(colorMap.default, DEFAULT_ENTRY_KEY) }
      : {}),
  };
};

// Restyles an ArcGIS layer in place (no refetch), along with the simplified
// features its overview draws when zoomed out (see startArcGISOverview).
const setArcGISLayerStyle = (layer, style) => {
  layer.setStyle(style);
  const overview = layer.get('arcgis_overview_layer');
  if (overview?.get('arcgis_overview') === 'features') {
    overview.setStyle(style);
  }
};

const getSLDForLayer = (
  layer_name,
  color_start,
  color_end,
  max,
  min,
  max_display,
  min_display
) => {
  let sld = `<?xml version="1.0" encoding="ISO-8859-1"?>
<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
<NamedLayer><Name>${layer_name}</Name><UserStyle><FeatureTypeStyle><Rule><RasterSymbolizer><Opacity>1.0</Opacity><ColorMap>`;

  sld += `<ColorMapEntry color="${color_start}" quantity="${Number(min_display) - 0.00001}" label="${Number(min_display) - 0.00001}" opacity="0" />`;
  sld += `<ColorMapEntry color="${color_start}" quantity="${min_display}" label="${min_display}" opacity="1" />`;

  sld += `<ColorMapEntry color="${color_end}" quantity="${max_display}" label="${max_display}" opacity="1" />`;
  sld += `<ColorMapEntry color="${color_end}" quantity="${Number(max_display) + 0.00001}" label="${Number(max_display) + 0.00001}" opacity="0" />`;
  sld += `<ColorMapEntry color="#FFFFFF" quantity="128" opacity="0" label="nodata"/>`;
  sld += `</ColorMap></RasterSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`;

  return sld;
};

const getSLDForLayerCatagorical = (layer_name, catData, nilValue) => {
  if (nilValue == -128) {
    nilValue = 128;
  }
  let sld = `<?xml version="1.0" encoding="ISO-8859-1"?>
  <StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
      xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
      xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
  <NamedLayer><Name>${layer_name}</Name><UserStyle><FeatureTypeStyle><Rule><RasterSymbolizer><Opacity>1.0</Opacity><ColorMap>`;
  catData
    .sort((a, b) => a.value - b.value)
    .forEach((data) => {
      sld += `<ColorMapEntry color="${data.color}" quantity="${Number(data.value)}" label="${Number(data.value)}" opacity="${data.visible ? 1 : 0}" />`;
    });
  if (nilValue) {
    sld += `<ColorMapEntry color="#FFFFFF" quantity="${Number(nilValue)}" opacity="0" label="nodata"/>`;
  }
  sld += ` </ColorMap></RasterSymbolizer></Rule>
  </FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`;
  console.log('sld', sld);
  return sld;
};

const getSLDForBoundaries = (layer_name, color) => {
  let sld = `<?xml version="1.0" encoding="ISO-8859-1"?><StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"><NamedLayer><Name>${layer_name}</Name><UserStyle><Name>counties</Name><FeatureTypeStyle><Rule><Name>rule1</Name><PolygonSymbolizer>`;
  sld += `<Stroke><CssParameter name="stroke">${color}</CssParameter></Stroke>`;
  sld += `</PolygonSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`;

  return sld;
};

const setBoundaryColor = (source, layerName, color) => {
  const sld = getSLDForBoundaries(layerName, color);
  source.setImageLoadFunction((image, src) => {
    const img = image.getImage();
    fetch(src, {
      method: 'POST',
      headers: { 'Content-Type': 'text/xml' },
      body: sld,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        const imageUrl = URL.createObjectURL(blob);
        img.onload = () => URL.revokeObjectURL(imageUrl);
        img.src = imageUrl;
      })
      .catch((error) => {
        console.error('Error loading the WMS layer:', error);
        // Still set a placeholder for the image to prevent errors
        img.src =
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      });
  });
};

const getLayerConfig = (url, layer) => {
  const list = config['Regional Resource Kit']['Southern California'];
  for (var j = 0; j < list.length; j++) {
    if (url === list[j].url && list[j].layer === layer) {
      return list[j];
    }
  }
  const list2 = config['Interagency Tracking System'];
  for (j = 0; j < list2.length; j++) {
    if (url === list2[j].url && list2[j].layer === layer) {
      return list2[j];
    }
  }
  return null;
};
const getMinMax = () => {
  let ranges = {};
  const list = config['Regional Resource Kit']['Southern California'];
  for (var j = 0; j < list.length; j++) {
    let key = list[j].url + '/' + list[j].layer;
    // @ts-ignore
    ranges[key] = [list[j].min, list[j].max];
  }
  const list2 = config['Interagency Tracking System'];
  for (j = 0; j < list2.length; j++) {
    let key = list2[j].url + '/' + list2[j].layer;
    // @ts-ignore
    ranges[key] = [list2[j].min, list2[j].max];
  }
  return ranges;
};
const getMin = (key) => {
  const list = config['Regional Resource Kit']['Southern California'];
  for (var j = 0; j < list.length; j++) {
    if (list[j].url + '/' + list[j].layer === key) {
      return list[j].min;
    }
  }
  const list2 = config['Interagency Tracking System'];
  for (j = 0; j < list2.length; j++) {
    if (list2[j].url + '/' + list2[j].layer === key) {
      return list2[j].min;
    }
  }
  return null;
};
const getMax = (key) => {
  const list = config['Regional Resource Kit']['Southern California'];
  for (var j = 0; j < list.length; j++) {
    if (list[j].url + '/' + list[j].layer === key) {
      return list[j].max;
    }
  }
  const list2 = config['Interagency Tracking System'];
  for (j = 0; j < list2.length; j++) {
    if (list2[j].url + '/' + list2[j].layer === key) {
      return list2[j].max;
    }
  }
  return null;
};
const getMainAttributes = (features) => {
  if (features) {
    let feature = features[0];
    const properties = feature.properties
      ? feature.properties
      : feature.getProperties();
    let candidates = [];
    let id = null;
    for (const key in properties) {
      if (key.toLowerCase().endsWith('id')) {
        id = key;
        continue;
      } else if (typeof feature.get(key) !== 'string') {
        continue;
      }
      candidates.push(key);
    }
    if (candidates) {
      if (candidates.length === 1) {
        return [candidates[0]];
      } else {
        // move the name to the first if exists
        let name = candidates.filter((item) => item.toLowerCase() === 'name');
        let rest = candidates.filter((item) => item.toLowerCase() !== 'name');
        candidates = name.concat(rest);

        for (let i = 0; i < candidates.length; i++) {
          let candidate = candidates[i];
          // check the uniqueness of candidate
          let values = new Set();
          let unique = true;
          features.forEach((feature) => {
            let value = feature.get(candidate);
            if (values.has(value)) {
              unique = false;
            } else {
              values.add(value);
            }
          });
          if (unique) {
            return [candidate];
          }
        }

        // found no candidate
        if (id) {
          return [id];
        } else {
          return candidates;
        }
      }
    } else {
      return [id];
    }
  }
  return [];
};

const isAddedVectorLayer = (item) => {
  return item instanceof VectorLayer;
};

// Map layer for a cached ArcGIS MapServer ("ArcGIS Tile Layer" resource, see
// getGisService). The tiles are Web Mercator XYZ like the basemap, and OL
// reprojects them into the map's EPSG:3310 view the same way. Outside the
// service's own LOD range tiles don't exist: past max_zoom OL upscales the
// last level, and below min_zoom it keeps requesting min_zoom tiles.
const createArcGISTileLayer = (gis_service, layerOptions = {}) => {
  return new TileLayer({
    ...(gis_service.extent_4326 && {
      extent: transformExtent(
        gis_service.extent_4326,
        'EPSG:4326',
        'EPSG:3310'
      ),
    }),
    ...layerOptions,
    layer_name: gis_service.layer_name,
    service_url: gis_service.service_url,
    source: new XYZ({
      url: gis_service.tile_url,
      crossOrigin: 'anonymous',
      minZoom: gis_service.min_zoom,
      maxZoom: gis_service.max_zoom,
    }),
  });
};

// --- Stitch-and-clip GeoTIFF export for "ArcGIS Tile Layer" datasets ---
// Cached tile services only serve rendered images, so this exports what the
// map shows (RGB plus alpha, transparent outside the boundary), not data
// values. It stays in the tiles' own Web Mercator grid so no pixel is
// resampled.
const WEB_MERCATOR_ORIGIN = 20037508.342787;
const WEB_MERCATOR_TILE_SIZE = 256;
const webMercatorResolution = (zoom) => 156543.03392800014 / 2 ** zoom;
// Largest image stitched (width * height), ~4096 x 4096 - 64MB of RGBA.
// Bigger boundaries get a coarser zoom rather than freezing the browser.
const MAX_TILE_EXPORT_PIXELS = 4096 * 4096;
const TILE_FETCH_CONCURRENCY = 8;

// A tile image, or null where the cache has none (outside its coverage the
// service answers 404 with an HTML page). Real tiles come back as
// application/octet-stream, so decode by content rather than content type.
const loadTileImage = async (url) => {
  const response = await fetch(url);
  if (!response.ok) return null;
  const blob = await response.blob();
  if (blob.type.startsWith('text/')) return null;
  return createImageBitmap(blob);
};

// Stitches the tiles covering clipGeometry (an ol Polygon/MultiPolygon in
// clipProjection) at the finest zoom that fits MAX_TILE_EXPORT_PIXELS, masks
// everything outside it, and encodes an EPSG:3857 GeoTIFF. Resolves to null
// when the service has no tiles within the boundary.
const exportArcGISTilesAsGeoTiff = async ({
  gis_service,
  clipGeometry,
  clipProjection,
}) => {
  const geometry = clipGeometry.clone().transform(clipProjection, 'EPSG:3857');
  let extent = geometry.getExtent();
  if (gis_service.extent_4326) {
    extent = getIntersection(
      extent,
      transformExtent(gis_service.extent_4326, 'EPSG:4326', 'EPSG:3857')
    );
    if (isEmpty(extent)) return null;
  }

  // Pixel window covering the extent at a zoom level, in that zoom's global
  // pixel grid (0,0 = top left of the world).
  const windowAt = (zoom) => {
    const res = webMercatorResolution(zoom);
    const x0 = Math.floor((extent[0] + WEB_MERCATOR_ORIGIN) / res);
    const y0 = Math.floor((WEB_MERCATOR_ORIGIN - extent[3]) / res);
    const x1 = Math.ceil((extent[2] + WEB_MERCATOR_ORIGIN) / res);
    const y1 = Math.ceil((WEB_MERCATOR_ORIGIN - extent[1]) / res);
    return { zoom, res, x0, y0, width: x1 - x0, height: y1 - y0 };
  };
  const minZoom = gis_service.min_zoom ?? 0;
  const maxZoom = gis_service.max_zoom ?? 19;
  let win = null;
  for (let zoom = maxZoom; zoom >= minZoom; zoom--) {
    const candidate = windowAt(zoom);
    if (candidate.width * candidate.height <= MAX_TILE_EXPORT_PIXELS) {
      win = candidate;
      break;
    }
  }
  if (!win) {
    throw new Error(
      "Boundary is too large to export at this layer's coarsest zoom"
    );
  }
  const { zoom, res, x0, y0, width, height } = win;

  const tiles = [];
  const size = WEB_MERCATOR_TILE_SIZE;
  for (
    let ty = Math.floor(y0 / size);
    ty <= Math.floor((y0 + height - 1) / size);
    ty++
  ) {
    for (
      let tx = Math.floor(x0 / size);
      tx <= Math.floor((x0 + width - 1) / size);
      tx++
    ) {
      tiles.push({ tx, ty });
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  let drawn = 0;
  const queue = [...tiles];
  const fetchWorker = async () => {
    while (queue.length) {
      const { tx, ty } = queue.shift();
      const url = gis_service.tile_url
        .replace('{z}', zoom)
        .replace('{y}', ty)
        .replace('{x}', tx);
      const image = await loadTileImage(url).catch(() => null);
      if (image) {
        ctx.drawImage(image, tx * size - x0, ty * size - y0);
        image.close();
        drawn++;
      }
    }
  };
  await Promise.all(
    Array.from({ length: TILE_FETCH_CONCURRENCY }, fetchWorker)
  );
  if (drawn === 0) return null;

  // Keep only pixels inside the boundary (even-odd so holes stay cut out).
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  const polygons =
    geometry.getType() === 'MultiPolygon'
      ? geometry.getCoordinates()
      : [geometry.getCoordinates()];
  polygons.forEach((rings) =>
    rings.forEach((ring) => {
      ring.forEach(([x, y], i) => {
        const px = (x + WEB_MERCATOR_ORIGIN) / res - x0;
        const py = (WEB_MERCATOR_ORIGIN - y) / res - y0;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
    })
  );
  ctx.fill('evenodd');

  const { data } = ctx.getImageData(0, 0, width, height);
  const tiff = writeArrayBuffer(data, {
    width,
    height,
    BitsPerSample: [8, 8, 8, 8],
    SampleFormat: [1, 1, 1, 1],
    SamplesPerPixel: 4,
    PhotometricInterpretation: 2, // RGB
    ExtraSamples: 2, // 4th band is unassociated alpha
    ModelPixelScale: [res, res, 0],
    ModelTiepoint: [
      0,
      0,
      0,
      x0 * res - WEB_MERCATOR_ORIGIN,
      WEB_MERCATOR_ORIGIN - y0 * res,
      0,
    ],
    GTModelTypeGeoKey: 1, // projected
    GTRasterTypeGeoKey: 1, // pixel is area
    ProjectedCSTypeGeoKey: 3857,
  });

  // Web Mercator pixels shrink on the ground away from the equator.
  const latitude = toLonLat(getCenter(extent))[1];
  return {
    tiff,
    zoom,
    width,
    height,
    tileCount: drawn,
    groundResolution: res * Math.cos((latitude * Math.PI) / 180),
  };
};

// Server-side stitch-and-clip of an ArcGIS tile layer with GDAL (the
// backend's POST /Utility/arcgis_tiles/clip). Unlike the browser version it
// isn't limited by browser memory, so large boundaries keep full detail, and
// the GeoTIFF comes back compressed and tiled. Resolves to the GeoTIFF bytes,
// or null when the layer has no tiles inside the boundary; rejects when the
// service is unavailable.
const clipArcGISTilesOnServer = async ({ gis_service, geojson, inputCrs }) => {
  const toZoom = (value) =>
    value === undefined ||
    value === null ||
    value === '' ||
    isNaN(Number(value))
      ? undefined
      : Number(value);
  // REACT_APP_TILE_CLIP_API_URL points just this endpoint at another backend
  // (e.g. a local datahub_services) while everything else uses the default.
  const apiUrl = (
    process.env.REACT_APP_TILE_CLIP_API_URL || process.env.REACT_APP_API_URL
  ).replace(/\/+$/, '');
  const response = await fetch(`${apiUrl}/Utility/arcgis_tiles/clip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tile_url: gis_service.tile_url,
      geojson,
      input_crs: inputCrs,
      min_zoom: toZoom(gis_service.min_zoom),
      max_zoom: toZoom(gis_service.max_zoom),
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // The route's own "nothing here" answer - not a missing endpoint, which
    // is also a 404.
    if (response.status === 404 && /no map tiles/i.test(body.detail || '')) {
      return null;
    }
    throw new Error(
      body.detail || `Tile clip service returned ${response.status}`
    );
  }
  const zip = await JSZip.loadAsync(await response.arrayBuffer());
  const tiff = Object.values(zip.files).find((file) =>
    file.name.endsWith('.tif')
  );
  if (!tiff) throw new Error('Tile clip service returned no GeoTIFF');
  return tiff.async('arraybuffer');
};

// GeoTIFF bytes of an ArcGIS tile layer clipped to clipGeometry (an ol
// Polygon/MultiPolygon in clipProjection), or null when it has no tiles
// there. Uses the backend when it's reachable, else stitches in the browser.
const exportArcGISTilesClipped = async ({
  gis_service,
  clipGeometry,
  clipProjection,
}) => {
  const inputCrs =
    typeof clipProjection === 'string'
      ? clipProjection
      : clipProjection.getCode();
  try {
    return await clipArcGISTilesOnServer({
      gis_service,
      geojson: new GeoJSON().writeGeometryObject(clipGeometry),
      inputCrs,
    });
  } catch (error) {
    console.warn(
      'Tile clip service unavailable, stitching in the browser:',
      error
    );
    const result = await exportArcGISTilesAsGeoTiff({
      gis_service,
      clipGeometry,
      clipProjection,
    });
    return result ? result.tiff : null;
  }
};

// --- Clicked-value estimates for "ArcGIS Tile Layer" datasets ---
// Cached tile services ("TilesOnly") have no identify/query, so the closest
// thing to a value is matching the clicked pixel's color against the
// service's own legend: a stretched ramp ("255 - 1", high value at the top)
// gives an interpolated number, separate swatches give a class label.

// Legend entry label of a stretched ramp: "<top value> - <bottom value>".
const RAMP_LABEL = /^\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*$/;

// Max RGB distance for a pixel to count as a legend color. Further than this
// it's something the legend doesn't describe (e.g. antialiased edges).
const MAX_LEGEND_COLOR_DISTANCE = 40;

const tileLegendCache = new Map();

const decodeLegendImage = async (entry) => {
  const img = new Image();
  img.src = `data:${entry.contentType};base64,${entry.imageData}`;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, img.width, img.height);
};

const pixelAt = (imageData, x, y) => {
  const i = (y * imageData.width + x) * 4;
  return Array.from(imageData.data.slice(i, i + 4));
};

// { type: 'ramp', stops: [{ color, value }], decimals } or
// { type: 'classes', classes: [{ color, label }] }, or null when the legend
// can't be read as either. Cached per service.
const loadTileLegendKey = (serviceUrl) => {
  if (!tileLegendCache.has(serviceUrl)) {
    const promise = (async () => {
      const json = await fetch(`${serviceUrl}/legend?f=json`).then((r) =>
        r.json()
      );
      const entries = (json.layers || []).flatMap((l) => l.legend || []);
      const ramp = entries.length === 1 && entries[0].label?.match(RAMP_LABEL);
      if (ramp) {
        const top = parseFloat(ramp[1]);
        const bottom = parseFloat(ramp[2]);
        const image = await decodeLegendImage(entries[0]);
        const x = Math.floor(image.width / 2);
        const stops = [];
        for (let y = 0; y < image.height; y++) {
          const color = pixelAt(image, x, y);
          if (color[3] < 255) continue;
          stops.push({
            color,
            value: top + ((bottom - top) * y) / (image.height - 1),
          });
        }
        const decimals = Math.max(
          (ramp[1].split('.')[1] || '').length,
          (ramp[2].split('.')[1] || '').length
        );
        return stops.length > 1 ? { type: 'ramp', stops, decimals } : null;
      }
      // A single unlabeled swatch would match everything - not a key.
      if (entries.length < 2) return null;
      const classes = await Promise.all(
        entries.map(async (entry) => {
          const image = await decodeLegendImage(entry);
          return {
            color: pixelAt(
              image,
              Math.floor(image.width / 2),
              Math.floor(image.height / 2)
            ),
            label: entry.label,
          };
        })
      );
      return { type: 'classes', classes };
    })().catch((error) => {
      console.error(`Failed to read tile legend for ${serviceUrl}:`, error);
      tileLegendCache.delete(serviceUrl);
      return null;
    });
    tileLegendCache.set(serviceUrl, promise);
  }
  return tileLegendCache.get(serviceUrl);
};

const colorDistanceSq = (a, b) =>
  (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

// Value along the ramp segment a->b closest to color: projects color onto
// the segment in RGB space so estimates fall between legend pixel rows.
const projectOntoSegment = (color, a, b) => {
  const d = [0, 1, 2].map((k) => b.color[k] - a.color[k]);
  const lengthSq = d[0] ** 2 + d[1] ** 2 + d[2] ** 2;
  let t = 0;
  if (lengthSq > 0) {
    t =
      [0, 1, 2].reduce((sum, k) => sum + (color[k] - a.color[k]) * d[k], 0) /
      lengthSq;
    t = Math.min(1, Math.max(0, t));
  }
  const point = [0, 1, 2].map((k) => a.color[k] + t * d[k]);
  return {
    value: a.value + t * (b.value - a.value),
    distanceSq: colorDistanceSq(color, point),
  };
};

// Estimate for a clicked tile pixel (RGBA from TileLayer#getData):
// { label, value } with value a number (ramp) or class name, or null when
// the pixel is empty or doesn't match the legend.
const estimateTileValue = async (serviceUrl, rgba) => {
  if (!rgba || rgba[3] === 0) return null;
  const key = await loadTileLegendKey(serviceUrl);
  if (!key) return null;
  const maxDistanceSq = MAX_LEGEND_COLOR_DISTANCE ** 2;

  if (key.type === 'ramp') {
    let best = null;
    for (let i = 0; i < key.stops.length - 1; i++) {
      const match = projectOntoSegment(rgba, key.stops[i], key.stops[i + 1]);
      if (!best || match.distanceSq < best.distanceSq) best = match;
    }
    if (best.distanceSq > maxDistanceSq) return null;
    return {
      label: 'Value',
      value: Number(best.value.toFixed(key.decimals)),
    };
  }

  let best = null;
  for (const entry of key.classes) {
    const distanceSq = colorDistanceSq(rgba, entry.color);
    if (!best || distanceSq < best.distanceSq) best = { entry, distanceSq };
  }
  if (best.distanceSq > maxDistanceSq) return null;
  return { label: 'Class', value: best.entry.label };
};

// One ArcGIS Feature Service item can bundle several layers, ingested as one
// CKAN dataset with one "ArcGIS Feature Service" resource per layer. The map
// (getGisService/getItemKey and everything keyed off them) works on one map
// layer per workspace entry, so a multi-layer dataset is split into one
// entry per layer resource when added to the map. Each entry keeps the
// parent's id - so the catalog still treats it as one dataset for its
// add/remove toggle - and carries only its own layer resource plus any
// non-layer resources (downloads etc.).
const expandDatasetForMap = (dataset) => {
  const isLayerResource = (resource) =>
    resource.format === 'ArcGIS Feature Service';
  const resources = dataset.resources || [];
  const layerResources = resources.filter(isLayerResource);
  if (layerResources.length <= 1) return [dataset];

  const otherResources = resources.filter((r) => !isLayerResource(r));
  return layerResources.map((resource) => ({
    ...dataset,
    title: `${dataset.title} - ${resource.name}`,
    resources: [resource, ...otherResources],
  }));
};

export {
  getSLDForLayer,
  getSLDForLayerCatagorical,
  getSLDForBoundaries,
  setBoundaryColor,
  getLayerConfig,
  getMax,
  getMin,
  getMinMax,
  getMainAttributes,
  isAddedVectorLayer,
  createArcGISVectorSource,
  createClippedArcGISVectorSource,
  queryArcGISFeaturesInGeometry,
  buildArcGISStyle,
  getArcGISLegendEntries,
  setArcGISLayerStyle,
  createArcGISTileLayer,
  expandDatasetForMap,
  getLayersNeedingZoom,
  attachArcGISOverview,
  getArcGISOverviewInfo,
  fetchArcGISTileFeatures,
  exportArcGISTilesAsGeoTiff,
  exportArcGISTilesClipped,
  estimateTileValue,
};
